import { useEffect, useRef, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import VideoPauseOverlayIcon from "@atlaskit/icon/core/video-pause-overlay";
import VideoPlayOverlayIcon from "@atlaskit/icon/core/video-play-overlay";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

export interface VideoProps {
    src: string;
    alt?: string;
    poster?: string;
    desktopHeight?: string;
    tabletHeight?: string;
    mobileHeight?: string;
    disableAutoplay?: boolean;
    showVideoControls?: boolean;
    loop?: boolean;
    playPauseButtonPosition?: "bottom-right" | "bottom-left";
}

export const Video = ({
    src,
    alt = "",
    poster,
    desktopHeight,
    tabletHeight,
    mobileHeight,
    disableAutoplay = false,
    showVideoControls = false,
    loop = false,
    playPauseButtonPosition = "bottom-right"
}: VideoProps) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [hasError, setHasError] = useState(false);

    // Play from beginning when in viewport, pause when out
    useEffect(() => {
        const video = videoRef.current;
        if (!video || disableAutoplay) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry && entry.isIntersecting && !hasError) {
                    video.currentTime = 0; // Always start from beginning
                    video.play().catch(() => {
                        // Ignore play errors when video is not ready or has issues
                        // Ignore error likely due to autoplay restrictions or browser power saving
                    });
                } else {
                    video.pause();
                }
            },
            { threshold: 0.5 }
        );

        observer.observe(video);

        return () => {
            observer.disconnect();
        };
    }, [hasError, disableAutoplay]);

    const togglePlay = () => {
        if (videoRef.current && !hasError) {
            if (isPlaying) {
                videoRef.current.pause();
                setIsPlaying(false);
            } else {
                videoRef.current
                    .play()
                    .then(() => {
                        setIsPlaying(true);
                    })
                    .catch(() => {
                        // Ignore error likely due to autoplay restrictions or browser power saving
                        setIsPlaying(false);
                    });
            }
        }
    };

    const videoCss = css({
        width: "100%",
        [media.above.md]: {
            height: desktopHeight ? desktopHeight : "auto"
        },
        [media.only.sm]: {
            height: tabletHeight ? tabletHeight : "auto"
        },
        [media.below.sm]: {
            height: mobileHeight ? mobileHeight : "auto"
        }
    });

    const intl = useIntl();

    return (
        <div css={css({ position: "relative" })}>
            <video
                ref={videoRef}
                playsInline
                muted={!disableAutoplay && !showVideoControls}
                preload="auto"
                poster={poster}
                controls={showVideoControls}
                loop={loop}
                css={videoCss}
                style={{
                    maxWidth: "100%"
                }}
                aria-label={alt}
                aria-hidden={alt === ""}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
                onError={() => setHasError(true)}
                onLoadStart={() => setHasError(false)}
            >
                <source src={src} type="video/mp4" />
                <track kind="captions" />
                Video not supported.
            </video>
            {!hasError && !showVideoControls && (
                <button
                    className={playPauseButtonPosition}
                    css={controlButtonCss}
                    onClick={togglePlay}
                >
                    <div css={buttonCircleCss}>
                        {isPlaying ? (
                            <VideoPauseOverlayIcon
                                label={intl.formatMessage({
                                    id: "video.pause",
                                    defaultMessage: "Pause"
                                })}
                            />
                        ) : (
                            <VideoPlayOverlayIcon
                                label={intl.formatMessage({
                                    id: "video.play",
                                    defaultMessage: "Play"
                                })}
                            />
                        )}
                    </div>
                </button>
            )}
        </div>
    );
};

const controlButtonCss = css({
    "position": "absolute",
    "insetBlockEnd": token("space.100"),
    "padding": token("space.075"),
    "height": "44px",
    "width": "44px",
    "backgroundColor": "transparent",
    "border": "none",
    "borderRadius": "100%",

    "&.bottom-left": {
        insetInlineStart: token("space.100")
    },
    "&.bottom-right": {
        insetInlineEnd: token("space.100")
    },
    ["&:hover"]: {
        cursor: "pointer"
    }
});

const buttonCircleCss = css({
    height: "32px",
    width: "32px",
    backgroundColor: wacToken("color.neutral.light400"),
    border: "none",
    borderRadius: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",

    ["&:hover"]: {
        cursor: "pointer",
        backgroundColor: wacToken("color.neutral.light500")
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        color: `${wacToken("color.neutral.light0")} !important`
    }
});
